import React, { useRef } from 'react'
import { Stack, Box, useTheme, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  CPCContainer,
  CPCCountry,
  CPCPercentageContainer,
  CPCPercentage,
  CPCValue,
  Carousel,
  UCPCardContainer,
  UCPButtonContainer,
  UCPButton
} from './WidgetAppUsage.styles'


interface AppData {
  name: string;
  count: number;
}

interface AppConfig {
  icon: string;
  backgroundColor: string;
  iconColor: string;
}

interface ContainerProps {
  children: React.ReactNode;
}

interface AppUsageCardProps {
  name: string;
  count: number;
  totalCount: number;
}

interface ScrollArrowProps {
  loading: boolean;
  scrollLeft: () => void;
  scrollRight: () => void;
}

interface AppUsagePercentageProps {
  appUseData: AppData[];
}

const Container: React.FC<ContainerProps> = ({ children }) => {
  const theme = useTheme()

  return (
    <Stack
      sx={{
        width: '100%',
        gap: '14px',
        border: '1px solid #dce1e5',
        borderRadius: '16px',
        padding: theme.spacing(1),
      }}
    >
      {children}
    </Stack>
  )
}

const AppUsageCard: React.FC<AppUsageCardProps> = ({ name, count, totalCount }) => {
  const percentage = ((count / totalCount) * 100).toFixed(1)
  const { icon, backgroundColor, iconColor } = getAppIconAndColors(name)

  return (
    <CPCContainer>
      <Box
        sx={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {icon.startsWith('fa') ? (
          <i className={icon} style={{ color: iconColor }} />
        ) : (
          <Typography style={{ color: iconColor, fontWeight: 500 }}>{icon}</Typography>
        )}
      </Box>
      <Box>
        <CPCCountry>{name}</CPCCountry>
        <CPCPercentageContainer>
          <CPCPercentage>{percentage}</CPCPercentage>
          <CPCValue>%</CPCValue>
        </CPCPercentageContainer>
      </Box>
    </CPCContainer>
  )
}

const ScrollArrow: React.FC<ScrollArrowProps> = ({ loading, scrollLeft, scrollRight }) => {
  return (
    <UCPButtonContainer>
      <UCPButton onClick={scrollLeft} disabled={loading}>
        <i className={'fa-regular fa-angle-left'} />
      </UCPButton>
      <UCPButton onClick={scrollRight} disabled={loading}>
        <i className={'fa-regular fa-angle-right'} />
      </UCPButton>
    </UCPButtonContainer>
  )
}

const getAppIconAndColors = (appName: string): AppConfig => {
  const appConfig: { [key: string]: AppConfig } = {
    'Netflix': { icon: 'fa-sharp fa-solid fa-n', backgroundColor: '#E50914', iconColor: 'white' },
    'Prime Video': { icon: 'fab fa-amazon', backgroundColor: '#3c78b4', iconColor: 'white' },
    'Spotify': { icon: 'fab fa-spotify', backgroundColor: '#1DB954', iconColor: 'white' },
    'YouTube': { icon: 'fab fa-youtube', backgroundColor: '#FF0000', iconColor: 'white' },
    'Facebook': { icon: 'fab fa-facebook-f', backgroundColor: '#1877F2', iconColor: 'white' },
    'TikTok': { icon: 'fab fa-tiktok', backgroundColor: '#FE2C55', iconColor: 'white' },
    'Twitter': { icon: 'fab fa-twitter', backgroundColor: '#1DA1F2', iconColor: 'white' },
    'PlayStation': { icon: 'fab fa-playstation', backgroundColor: '#003087', iconColor: 'white' },
    'Xbox': { icon: 'fab fa-xbox', backgroundColor: '#52B043', iconColor: 'white' },
    'Instagram': { icon: 'fab fa-instagram', backgroundColor: '#5851DB', iconColor: 'white' },
  }

  if (appConfig[appName]) {
    return appConfig[appName];
  }

  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F06292', '#AED581', '#FFD54F', '#4DB6AC', '#7986CB'];
  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  
  return {
    icon: appName.charAt(0).toUpperCase(),
    backgroundColor: randomColor,
    iconColor: 'white'
  };
}

const AppUsagePercentage: React.FC<AppUsagePercentageProps> = ({ appUseData }) => {
  const { t } = useTranslation()
  const cardRef = useRef<HTMLDivElement>(null)

  const totalCount = appUseData.reduce((sum, app) => sum + app.count, 0)

  const scrollLeft = () => {
    cardRef.current?.scrollBy({ left: -400, top: 0, behavior: 'smooth' })
  }

  const scrollRight = () => {
    cardRef.current?.scrollBy({ left: 400, top: 0, behavior: 'smooth' })
  }

  return (
    <Container>
      <Typography sx={{fontWeight: '500', padding: '10px 20px'}}>
        {t('statistics.chromecasts.top_app_use')}
      </Typography>
      <Carousel>
        <UCPCardContainer ref={cardRef}>
          {appUseData.map(({ name, count }) => (
            <AppUsageCard
              key={name}
              name={name}
              count={count}
              totalCount={totalCount}
            />
          ))}
        </UCPCardContainer>
        <ScrollArrow
          loading={false}
          scrollLeft={scrollLeft}
          scrollRight={scrollRight}
        />
      </Carousel>
    </Container>
  )
}

export default AppUsagePercentage